<template>
  <div class="container-fluid">
    <page-header
      :page="page"
      has-return
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    >
      <template v-slot:customActions>
        <ActionButton
          v-show="userCanEdit"
          class="m-1"
          text="Guardar"
          variant="success"
          i="fas fa-save"
          @click="saveChanges"
        />
      </template>
    </page-header>
    <div class="d-flex">
      <SectionTitle
        has-line
        class="section-title-container"
        content="Información del Libro"
      />
    </div>
    <ValidationObserver ref="formValidator">
      <div class="row">
        <div class="col-md-4 col-12">
          <ValidationProvider v-slot="{ errors }" rules="required">
            <custom-input
              id="txtTitulo"
              v-model="book.title"
              label="Título:"
              :label-class="isMobile ? 'text-title' : ''"
              :error="errors[0]"
              :custom-error-msg="getError('title')"
              :disabled="!userCanEdit"
            />
          </ValidationProvider>
        </div>
        <div :class="['col-md-4 col-12', { 'mt-3': isMobile }]">
          <ValidationProvider
            v-slot="{ errors }"
            rules="mustHaveSelectedOption"
          >
            <custom-drop-down
              v-if="userCanEdit"
              id="txtAutor"
              v-model="book.author"
              :options="collections.authors"
              label="Autor:"
              :regular-label="isMobile"
              fieldkey="id"
              fieldname="name"
              placeholder="Seleccione"
              class="height-fixed"
              :error="errors[0]"
              :custom-error-msg="getError('author')"
              :disabled="!userCanEdit"
            />
            <custom-input-display
              v-else
              v-model="book.author.name"
              label="Autor:"
            />
          </ValidationProvider>
        </div>
        <div :class="['col-md-4 col-12', { 'mt-3': isMobile }]">
          <ValidationProvider v-slot="{ errors }" rules="required">
            <custom-input
              id="txtISBN"
              v-model="book.isbn"
              label="ISBN:"
              :label-class="isMobile ? 'text-title' : ''"
              :error="errors[0]"
              :custom-error-msg="getError('ISBN')"
              :disabled="!userCanEdit"
            />
          </ValidationProvider>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4 col-12 mt-3">
          <ValidationProvider
            v-slot="{ errors }"
            rules="mustHaveSelectedOption"
          >
            <custom-drop-down
              v-if="userCanEdit"
              id="txtCasaEditora"
              v-model="book.publisher"
              :options="collections.publishers"
              label="Casa Editorial:"
              :regular-label="isMobile"
              fieldkey="id"
              fieldname="name"
              placeholder="Seleccione"
              class="height-fixed"
              :error="errors[0]"
              :custom-error-msg="getError('publisher')"
              :disabled="!userCanEdit"
            />
            <custom-input-display
              v-else
              v-model="book.publisher.name"
              label="Casa Editora:"
            />
          </ValidationProvider>
        </div>
        <div class="col-md-4 col-12 mt-3">
          <ValidationProvider
            v-slot="{ errors }"
            rules="mustHaveSelectedOption"
          >
            <custom-drop-down
              v-if="userCanEdit"
              id="ddlMateria"
              v-model="book.subject"
              :options="collections.subjects"
              label="Materia:"
              :regular-label="isMobile"
              fieldkey="id"
              fieldname="name"
              placeholder="Seleccione"
              class="height-fixed"
              :error="errors[0]"
              :custom-error-msg="getError('subject')"
              :disabled="!userCanEdit"
            />
            <custom-input-display
              v-else
              v-model="book.subject.name"
              label="Materia:"
            />
          </ValidationProvider>
        </div>
        <div class="col-md-4 col-12 mt-3">
          <ValidationProvider
            v-slot="{ errors }"
            rules="mustHaveSelectedOption"
          >
            <custom-drop-down
              v-if="userCanEdit"
              id="ddlGrado"
              v-model="book.grade"
              :options="collections.grades"
              label="Grado:"
              :regular-label="isMobile"
              fieldkey="id"
              fieldname="name"
              placeholder="Seleccione"
              class="height-fixed"
              :error="errors[0]"
              :custom-error-msg="getError('grade')"
              :disabled="!userCanEdit"
            />
            <custom-input-display
              v-else
              v-model="book.grade.name"
              label="Grado:"
            />
          </ValidationProvider>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import ActionButton from 'ActionButton';
import SectionTitle from 'SectionTitle';
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate';
import CustomInput from 'CustomInput';
import CustomDropDown from 'custom-drop-down';
import CustomInputDisplay from 'CustomInputDisplay';
import sortBy from 'lodash/sortBy';
import EduEspCode from '@/utils/constants/eduEspCode';
import academicService from '@/services/api/academicUtils.api.js';
import { getBook } from '@/modules/books-requests/api';
import alert from 'utils/mixins/alert.mixin.js';
import EduEspTextCodes from "@/utils/constants/EduEspTextCodes";
import {
  addBook,
  editBook,
  getBooksAuthors,
  getBooksPublishers,
} from '@/modules/books-requests/api';

const BOOK_REQUEST_SEARCH_ROUTE_NAME = 'booksCatalog';
const OPTION_EMPTY = { name: '', id: 0 };

// Extended rules
extend('mustHaveSelectedOption', {
  validate(value) {
    if (value) return value.id && value.id != 0;
    return false;
  },
  computesRequired: true,
});
// Extended rules

export default {
  name: 'BooksDocumentation',
  components: {
    PageHeader,
    ActionButton,
    SectionTitle,
    ValidationObserver,
    ValidationProvider,
    CustomInput,
    CustomDropDown,
    CustomInputDisplay,
  },
  mixins: [alert],
  computed: {
    isMobile() {
      return this.$store.getters.getIsMobileDensity;
    },
  },
  data: () => ({
    page: {
      icon: 'fas fa-book-open',
      title: 'Cátalogo de Libros',
      routes: ['Inicio', 'Libros', 'Cátalogo de Libros'],
    },
    collections: {
      subjects: [],
      grades: [],
      authors: [],
      publishers: [],
    },
    book: {
      BookId: null,
      title: null,
      isbn: null,
      author: OPTION_EMPTY,
      publisher: OPTION_EMPTY,
      readingMedium: OPTION_EMPTY,
      subject: OPTION_EMPTY,
      grade: OPTION_EMPTY,
    },
    userCanEdit: false,
  }),
  methods: {
    goBack() {
      this.$router.push({ name: BOOK_REQUEST_SEARCH_ROUTE_NAME });
    },
    getError(code) {
      const errors = {
        title: 'El título es requerido',
        author: 'El autor es requerido',
        ISBN: 'El ISBN es requerido',
        publisher: 'La Casa Editorial es requerida',
        readingMedium: 'El medio de lectura es requerido',
        subject: 'La materia es requerida',
        grade: 'El grado es requerido',
      };
      return errors[code];
    },
    async saveChanges() {
      if (this.book.isbn.length > 13) {
        this.ShowToast(
          'Alerta',
          'Favor verificar el número del ISBN, el límite es de 13 caracteres.',
          'error'
        );
        return;
      }
      const isFormValid = await this.$refs.formValidator.validate();
      if (!isFormValid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la Información solicitada.',
          'error'
        );
        return;
      }
      const book = {
        title: this.book.title,
        isbn: this.book.isbn,
        authorId: this.book.author.id,
        publisherId: this.book.publisher.id,
        subjectId: this.book.subject.id,
        gradeId: this.book.grade.id,
      };

      try {
        if (this.book.BookId) {
          book['BookId'] = parseInt(this.book.BookId);
          await editBook(book);
        } else {
          await addBook(book);
        }

        this.ShowToast(
          'Alerta',
          'La Información ha sido guardada exitosamente.',
          'success'
        );
        this.$router.push({ name: BOOK_REQUEST_SEARCH_ROUTE_NAME });
      } catch (error) {
        this.ShowToast('Alerta', error.response.data.message, 'error');
      }
    },
    async fetchDropDownsData() {
      const subjectsResult = await academicService.getEduEsp({
        codetype: EduEspCode.CatlibSubject,
      });

      const gradeRange = { first: 3, twelveth: 14 };
      const gradesResult = await academicService.getGrades({
        inferiorLimitId: gradeRange.first,
        superiorLimitId: gradeRange.twelveth,
      });

      const authorsResult = await getBooksAuthors();
      const publishersResult = await getBooksPublishers();

      this.collections.subjects = sortBy(subjectsResult.data, ['name']);
      this.collections.grades = gradesResult.data;
      this.collections.authors = authorsResult.data.filter(
        (e) => e.name.toUpperCase() !== EduEspTextCodes.NotAvailable
      );
      this.collections.publishers = publishersResult.data.filter(
        (e) => e.name.toUpperCase() !== EduEspTextCodes.NotAvailable
      );
    },
    async fetchBook(bookId) {
      const { data: book } = await getBook({ bookId });

      this.book = {
        BookId: bookId,
        title: book.title,
        author:
          this.collections.authors.find((f) => f.name == book.author) ||
          OPTION_EMPTY,
        isbn: book.isbn,
        publisher:
          this.collections.publishers.find((f) => f.name === book.publisher) ||
          OPTION_EMPTY,
        readingMedium: OPTION_EMPTY,
        subject:
          this.collections.subjects.find((f) => f.name === book.subject) ||
          OPTION_EMPTY,
        grade:
          this.collections.grades.find((f) => f.name === book.grade) ||
          OPTION_EMPTY,
      };
    },
  },
  async mounted() {
    await this.fetchDropDownsData();

    const isReadOnly = this.$route.name === 'ReadBooksCatalog';
    this.userCanEdit = !isReadOnly;

    const bookId = this.$route.params.id;
    if (bookId) {
      await this.fetchBook(bookId);
    } else {
      this.book.author = null;
      this.book.publisher = null;
      this.book.readingMedium = null;
      this.book.subject = null;
      this.book.grade = null;
    }
  },
};
</script>

<style scoped>
::v-deep .text-title {
  font-size: 12px !important;
  font-weight: normal !important;
}

.section-title-container {
  width: 100%;
}

::v-deep .input-display > label {
  font-weight: bold !important;
  padding-bottom: 3px !important;
}

@media only screen and (max-width: 375px) {
  .section-title-container {
    margin-bottom: 2px !important;
  }

  ::v-deep .tag-badge-title-info {
    font-size: 12px !important;
  }

  ::v-deep .vs__search {
    font-size: 16px !important;
  }

  ::v-deep .vs__selected {
    font-size: 12px !important;
  }

  ::v-deep .vs__dropdown-menu {
    font-size: 12px !important;
  }

  ::v-deep .data-name {
    font-size: 12px !important;
    font-weight: normal;
  }

  ::v-deep label {
    font-size: 12px !important;
  }

  ::v-deep .student-search-alert-error.no-border {
    padding-top: 5px !important;
  }

  ::v-deep .custom-class {
    width: initial !important;
  }
}
</style>
